import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }
    setFormState('submitting');
    setFormError('');
    try {
      const payload = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9qskqknlsngrm4lm590', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormState('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormState('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const timeline = [
    { year: '2018', title: '平台创立', desc: '黑马磁力搜索平台正式上线，专注于磁力链接搜索服务' },
    { year: '2019', title: '突破百万', desc: '日活跃用户突破100万，成为国内知名磁力搜索平台' },
    { year: '2020', title: '技术升级', desc: '全面升级搜索引擎，引入AI智能推荐算法，搜索准确率提升40%' },
    { year: '2021', title: '品类扩展', desc: '资源品类从4大类扩展至8大类，覆盖用户多元需求' },
    { year: '2022', title: '数据爆发', desc: '磁力资源索引突破500万，日均搜索量突破300万次' },
    { year: '2023', title: '体验优化', desc: '推出智能搜索建议、资源热度排行等全新功能' },
    { year: '2024', title: '行业领先', desc: '稳居国内磁力搜索行业前三，用户满意度达98.5%' },
    { year: '2025', title: 'AI赋能', desc: '全面AI化升级，智能语义搜索、个性化资源推荐' },
    { year: '2026', title: '全新启航', desc: '全新品牌升级，以更强大的搜索能力和更优质的用户体验服务全球用户' },
  ];

  const testimonials = [
    { name: '张先生', role: '影视爱好者', content: '用了黑马磁力三年了，搜索资源真的又快又准。尤其是4K电影资源，几乎都能找到，画质还特别好。强烈推荐！' },
    { name: '李女士', role: '音乐发烧友', content: '作为一个音乐发烧友，黑马磁力上的无损音乐资源真的太丰富了。FLAC、DSD格式的资源品质特别好，帮我省了不少买CD的钱。' },
    { name: '王同学', role: '在校大学生', content: '准备考研期间，在黑马磁力上找到了很多高质量的学习资料和电子书。界面干净没有乱七八糟的广告，体验非常好。' },
    { name: '刘先生', role: 'IT从业者', content: '找软件资源特别方便，各种开发工具、设计软件都能搜到。资源更新速度很快，新版本基本隔天就能找到。' },
    { name: '陈女士', role: '自由职业者', content: '黑马磁力是我每天必用的工具之一。无论是找电影、音乐还是工作需要的素材，都能快速找到。搜索体验确实一流！' },
    { name: '赵先生', role: '游戏玩家', content: '游戏资源丰富度令人惊喜。3A大作基本都有，而且版本更新速度快。配合推荐的下载工具，速度也很给力。' },
  ];

  return (
    <>
      <SEOHead
        title="关于黑马磁力 - 品牌介绍与联系方式"
        description="黑马磁力是专业的磁力链接搜索平台，自2018年创立以来，已服务超过120万日活跃用户。了解黑马磁力的品牌故事、发展历程与联系方式。"
        keywords="黑马磁力,关于黑马磁力,磁力搜索平台,黑马磁力联系方式"
        canonicalUrl="https://heimacili.com/about"
      />
      <SchemaOrg type="Organization" data={{
        name: '黑马磁力',
        url: 'https://heimacili.com',
        description: '专业的磁力链接搜索与资源发现平台',
        foundingDate: '2018',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@heimacili.com',
        },
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: [
          { '@type': 'Question', name: '黑马磁力搜索是否收费？', acceptedAnswer: { '@type': 'Answer', text: '黑马磁力完全免费使用，所有搜索功能均不收取任何费用。' } },
          { '@type': 'Question', name: '如何联系我们？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面的联系表单或发送邮件至 contact@heimacili.com 与我们取得联系。' } },
        ],
      }} />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]} />

        {/* Hero */}
        <section className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20interior%20with%20warm%20lighting%2C%20clean%20workspace%20design%2C%20natural%20elements%2C%20professional%20creative%20atmosphere%2C%20soft%20ambient%20light%2C%20architectural%20photography&width=1920&height=600&seq=about-hero-001&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/40"></div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-4">关于黑马磁力</h1>
            <p className="text-background-50/80 text-sm md:text-base leading-relaxed">
              自2018年创立以来，黑马磁力始终致力于为用户提供最专业、最高效的磁力链接搜索服务。
              我们以技术创新为驱动，以用户体验为核心，不断优化搜索算法，扩展资源覆盖范围。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-12">品牌故事</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打破信息壁垒，让每个人都能轻松获取数字资源。黑马磁力通过先进的技术手段，将分散在网络各处的磁力资源进行高效聚合与精准匹配，为用户提供一站式的磁力搜索体验。
                </p>
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为全球领先的去中心化资源发现平台。我们相信未来的资源获取方式将更加开放、自由、高效，黑马磁力将持续投入技术创新，推动这一愿景的实现。
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {[
                { label: '成立年份', value: '2018年' },
                { label: '资源索引', value: '850万+' },
                { label: '日活用户', value: '120万+' },
                { label: '满意率', value: '98.5%' },
              ].map((s) => (
                <div key={s.label} className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{s.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block"></div>
              <div className="space-y-8">
                {timeline.map((item, idx) => (
                  <div key={item.year} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 hidden md:block"></div>
                    <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 flex items-center justify-center z-10">
                      <div className="w-2 h-2 rounded-full bg-background-50"></div>
                    </div>
                    <div className="md:w-1/2 pl-10 md:pl-0">
                      <div className="bg-background-50 border border-background-200 rounded-lg p-5">
                        <span className="text-xs font-bold text-primary-500">{item.year}</span>
                        <h4 className="font-heading text-base font-bold text-foreground-900 mt-1">{item.title}</h4>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-50 border border-background-200 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-sm text-primary-400"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-accent-700">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">联系我们</h2>
            <p className="text-foreground-500 text-sm text-center mb-10">如有任何问题或建议，欢迎通过以下方式与我们联系</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">电子邮件</h4>
                <p className="text-xs text-foreground-500">contact@heimacili.com</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-wechat-line text-xl text-accent-600"></i>
                </div>
                <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">黑马磁力搜索</p>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 text-center">
                <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-weibo-line text-xl text-secondary-600"></i>
                </div>
                <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1">官方微博</h4>
                <p className="text-xs text-foreground-500">@黑马磁力</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">发送消息</h3>
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formState === 'submitting' ? '发送中...' : '发送消息'}
                </button>
                {formState === 'success' && (
                  <p className="mt-3 text-sm text-accent-600">消息已成功发送，感谢您的联系！</p>
                )}
                {formState === 'error' && (
                  <p className="mt-3 text-sm text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}